import React from 'react';
import {
  Input,
  type InputProps,
  type InputRef,
  Select,
  Space,
  Typography,
} from 'antd';
import {
  defaultCountries as origDefaultCountries,
  FlagImage,
  usePhoneInput,
} from 'react-international-phone';

import { intlTel } from '../../services/intlTel';
import { useCompany } from '../../shell';

import './phoneInput.scss';

/**
 * This component is based on example:
 * https://github.com/goveo/react-international-phone/blob/master/src/stories/UiLibsExample/components/AntPhone.tsx
 */

export interface PhoneInputProps
  extends Omit<InputProps, 'value' | 'onChange'> {
  defaultCountry?: string;
  value?: string;
  onChange?: (phone: string) => void;
}

const EU_COUNTRIES = new Set([
  'al',
  'ad',
  'at',
  'by',
  'be',
  'ba',
  'bg',
  'hr',
  'cz',
  'dk',
  'ee',
  'fo',
  'fi',
  'fr',
  'de',
  'gi',
  'gr',
  'va',
  'hu',
  'is',
  'ie',
  'it',
  'lv',
  'li',
  'lt',
  'lu',
  'mk',
  'mt',
  'md',
  'mc',
  'me',
  'nl',
  'no',
  'pl',
  'pt',
  'ro',
  'ru',
  'sm',
  'rs',
  'sk',
  'si',
  'es',
  'se',
  'ch',
  'ua',
  'gb',
]);

const defaultCountries = origDefaultCountries
  .filter(([, iso2]) => EU_COUNTRIES.has(iso2))
  .map((country) => {
    // UA dial code is officially +380, but Ukrainian users habitually type a
    // leading 0 after the country code. Shifting the boundary to +38 lets them
    // enter "0XX XXX XXXX" naturally while the stored value stays +380XXXXXXXXX.
    if (country[1] === 'ua') {
      return [
        country[0],
        country[1],
        '38',
        '(...) ... .. ..',
        ...country.slice(4),
      ] as typeof country;
    }

    return country;
  });

export const PhoneInput: React.FC<PhoneInputProps> = ({
  value,
  onChange,
  defaultCountry,
  ...inputProps
}) => {
  const company = useCompany();
  const country = defaultCountry || company.country.toLowerCase();

  // usePhoneInput treats any leading digits as a dial code, so a local-format
  // value like "0631234567" would be misparsed as "+06...". Normalize it to
  // E164 (async — intlTel lazy-loads its utils) before handing it to the hook.
  const [normalizedValue, setNormalizedValue] = React.useState(value);

  React.useEffect(() => {
    if (!value || value.startsWith('+')) {
      setNormalizedValue(value);

      return;
    }

    let cancelled = false;

    intlTel.parseNumber(value, { country }).then((parsed) => {
      if (!cancelled) {
        setNormalizedValue(parsed ?? value);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [value, country]);

  const phoneInput = usePhoneInput({
    defaultCountry: country,
    countries: defaultCountries,
    value: normalizedValue,
    onChange: (data) => {
      if (onChange) {
        const prevValue = value || '';
        const newValue =
          data.phone === `+${data.country.dialCode}`
            ? // treat bare dial code (e.g. "+38" for UA) as empty
              ''
            : data.phone;

        if (prevValue !== newValue) {
          onChange(newValue);
        }
      }
    },
  });

  const inputRef = React.useRef<InputRef>(null);

  // Need to reassign inputRef because antd provides not default ref
  React.useEffect(() => {
    if (phoneInput.inputRef) {
      phoneInput.inputRef.current = inputRef.current?.input || null;
    }
  }, [inputRef.current?.input, phoneInput.inputRef]);

  return (
    <Input
      type="tel"
      autoComplete="tel"
      addonBefore={
        <Select
          showSearch
          optionFilterProp="labelForFilter"
          popupMatchSelectWidth={false}
          value={phoneInput.country.iso2}
          onChange={(newIso2) => phoneInput.setCountry(newIso2)}
          options={defaultCountries.map(([name, iso2, dialCode]) => ({
            label: <FlagImage iso2={iso2} />,
            dropdownLabel: (
              <Space>
                <FlagImage iso2={iso2} />
                <span>{name}</span>
                <Typography.Text type="secondary">+{dialCode}</Typography.Text>
              </Space>
            ),
            labelForFilter: `${iso2} ${name} ${dialCode}`,
            value: iso2,
          }))}
          optionRender={(option) => option.data.dropdownLabel}
        />
      }
      {...inputProps}
      value={phoneInput.inputValue}
      onChange={(event) => {
        // a hotfix for https://github.com/react-component/input/issues/62
        // rc-input creates a copy of input element instead of passing original
        // DOM node. Which makes impossible to control cursor position
        const currentTarget = inputRef.current!.input;

        event = Object.create(event, {
          target: { value: currentTarget },
          currentTarget: { value: currentTarget },
        });

        phoneInput.handlePhoneValueChange(event);
      }}
      ref={inputRef}
    />
  );
};

/*
// field implementation example. May be move into component in future
const { _ } = useLingui();

<Form.Item
  name="point"
  label={<Trans id="settings.appeal.sources.phone.number">Phone number</Trans>}
  required
  normalize={(value) => value.replace(/^\+/, '')}
  validateTrigger="onBlur"
  rules={[
    {
      required: true,
    },
    {
      async validator(_noop, value) {
        const isValid = await intlTel.isValid(`+${value}`);

        if (!isValid) {
          throw new Error(
            _(
              msg({
                id: 'general.invalid.phone.number',
                message: 'Invalid phone number',
              }),
            ),
          );
        }
      },
    },
  ]}
>
  <PhoneInput />
</Form.Item>;
*/
